import React, { useState, useRef, useEffect } from 'react';
import { exportToCSV, exportToExcel } from '../../utils/export';

/**
 * ExportButton - Pulsante per esportazione dati con dropdown
 *
 * Props:
 * - data: Array di dati da esportare
 * - columns: Configurazione colonne [{key, label, format?}]
 * - filename: Nome del file (senza estensione)
 * - disabled: boolean
 * - onExport: (format) => void - callback opzionale
 */
function ExportButton({
  data = [],
  columns = [],
  filename = 'export',
  disabled = false,
  onExport
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const dropdownRef = useRef(null);

  // Chiudi dropdown su click esterno
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (format) => {
    if (data.length === 0) return;

    setExporting(true);
    try {
      if (format === 'csv') {
        exportToCSV(data, columns, filename);
      } else if (format === 'excel') {
        exportToExcel(data, columns, filename);
      }

      if (onExport) {
        onExport(format);
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
      setIsOpen(false);
    }
  };

  const isDisabled = disabled || data.length === 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`
          inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
          border rounded-lg transition-colors
          ${isDisabled
            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }
        `}
      >
        {exporting ? (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        )}
        Esporta
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-10">
          <div className="py-1">
            <button
              onClick={() => handleExport('csv')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div>
                <div className="font-medium">CSV</div>
                <div className="text-xs text-gray-500">Valori separati da virgola</div>
              </div>
            </button>

            <button
              onClick={() => handleExport('excel')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              <div>
                <div className="font-medium">Excel</div>
                <div className="text-xs text-gray-500">Foglio di calcolo</div>
              </div>
            </button>
          </div>

          <div className="border-t px-4 py-2">
            <p className="text-xs text-gray-500">
              {data.length} {data.length === 1 ? 'record' : 'record'} da esportare
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExportButton;
